import { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts'
import type { CSSProperties } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useAdminRequests } from '../hooks/useAdminRequests'
import type { ServiceRequestDocument } from '@/types'

const tooltipContentStyle: CSSProperties = {
  background: 'var(--popover)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--popover-foreground)',
}
const tooltipLabelStyle: CSSProperties = { color: 'var(--popover-foreground)' }
const tooltipItemStyle: CSSProperties = { color: 'var(--popover-foreground)' }

type RequestWithId = ServiceRequestDocument & { id: string }

function parseQuoteTotal(raw: string): number {
  const n = parseFloat(raw.replace(/[^0-9.]/g, ''))
  return isNaN(n) ? 0 : n
}

function getMonthKey(val: unknown): string | null {
  try {
    let date: Date | null = null
    if (val instanceof Date) date = val
    else if (typeof val === 'object' && val !== null && 'toDate' in val) {
      date = (val as { toDate: () => Date }).toDate()
    }
    if (!date) return null
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
  } catch {
    return null
  }
}

function buildMonthlyData(requests: RequestWithId[]) {
  const now = new Date()
  const months: { key: string; label: string }[] = []
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleString('default', { month: 'short', year: '2-digit' })
    months.push({ key, label })
  }
  const counts: Record<string, number> = {}
  for (const { key } of months) counts[key] = 0
  for (const r of requests) {
    const k = getMonthKey(r.createdAt)
    if (k && k in counts) counts[k] = (counts[k] ?? 0) + 1
  }
  return months.map(({ key, label }) => ({ month: label, count: counts[key] }))
}

function buildStatusData(requests: RequestWithId[]) {
  const counts = { pending: 0, reviewed: 0, declined: 0, converted: 0 }
  for (const r of requests) counts[r.status]++
  return [
    { name: 'Pending',   value: counts.pending,   fill: 'var(--chart-2)' },
    { name: 'Reviewed',  value: counts.reviewed,  fill: 'var(--chart-1)' },
    { name: 'Converted', value: counts.converted, fill: 'var(--chart-3)' },
    { name: 'Declined',  value: counts.declined,  fill: 'var(--chart-4)' },
  ].filter((d) => d.value > 0)
}

function buildServiceData(requests: RequestWithId[]) {
  const counts: Record<string, number> = {}
  for (const r of requests) {
    for (const svc of r.services) {
      counts[svc.serviceKey] = (counts[svc.serviceKey] ?? 0) + 1
    }
  }
  return Object.entries(counts)
    .map(([key, count]) => ({ service: key, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8)
}

function thisMonthCount(requests: RequestWithId[]): number {
  const now = new Date()
  const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  return requests.filter((r) => {
    const k = getMonthKey(r.createdAt)
    return k === key && (r.status === 'reviewed' || r.status === 'declined')
  }).length
}

interface StatCardProps {
  title: string
  value: string | number
  loading: boolean
}

function StatCard({ title, value, loading }: StatCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-9 w-24" />
        ) : (
          <p className="text-4xl font-bold">{value}</p>
        )}
      </CardContent>
    </Card>
  )
}

export function ProjectsDashboardPage() {
  const { requests, loading, error } = useAdminRequests()

  const pipeline = useMemo(() => {
    return requests
      .filter((r) => r.status === 'pending' || r.status === 'reviewed')
      .reduce((sum, r) => sum + parseQuoteTotal(r.quoteTotal), 0)
  }, [requests])

  const pendingCount = useMemo(
    () => requests.filter((r) => r.status === 'pending').length,
    [requests],
  )

  const resolvedThisMonth = useMemo(() => thisMonthCount(requests), [requests])

  const monthlyData = useMemo(() => buildMonthlyData(requests), [requests])
  const statusData  = useMemo(() => buildStatusData(requests),  [requests])
  const serviceData = useMemo(() => buildServiceData(requests), [requests])

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Projects Dashboard</h1>
        <p className="text-muted-foreground mt-1">Request trends and pipeline overview.</p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total Requests"       value={requests.length}                                      loading={loading} />
        <StatCard title="Pending"              value={pendingCount}                                         loading={loading} />
        <StatCard title="Revenue Pipeline"     value={`$${pipeline.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} loading={loading} />
        <StatCard title="Resolved This Month"  value={resolvedThisMonth}                                    loading={loading} />
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Requests over time */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Requests Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-48 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthlyData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                  <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ fill: 'var(--muted)', opacity: 0.3 }} />
                  <Bar dataKey="count" name="Requests" fill="var(--chart-1)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Requests by status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Requests by Status</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-center">
            {loading ? (
              <Skeleton className="h-48 w-48 rounded-full" />
            ) : statusData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-16">No data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ fill: 'var(--muted)', opacity: 0.3 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top services */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top Requested Services</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : serviceData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8">No data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(160, serviceData.length * 36)}>
              <BarChart
                data={serviceData}
                layout="vertical"
                margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="service" width={100} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ fill: 'var(--muted)', opacity: 0.3 }} />
                <Bar dataKey="count" name="Requests" fill="var(--chart-3)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
